// URLs of pages that were replaced, and where they open now. Each `to` gets the route params
// and the query string, and carries over the keys the old page read and the API honored.
// Entries with `when` share their path with a live page and redirect only when it returns
// true for the query string; `exact` keeps them off the entity pages below that path.

import { cvePath, hostPath, packagePath } from './entityRoutes';

// Adds the non-empty values to a path that may already have a query string.
const withQuery = (path, values) => {
  const extra = new URLSearchParams(Object.entries(values).filter(([, value]) => value)).toString();
  if (!extra) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${extra}`;
};

const oneOf = (value, allowed) => (allowed.includes(value) ? value : '');

// The package drill-downs identified the package by id; without one only the name is left.
const packageTab = (query, tab, values = {}) => {
  const id = query.get('package_id') || query.get('package_uuid');
  if (id) return withQuery(packagePath({ id }, tab), values);
  const name = query.get('package_name');
  return name ? packagePath({ name }) : '/packages';
};

const LEGACY_REDIRECTS = [
  { path: '/investigation', to: (params, query, search) => `/query${search}` },
  { path: '/outdated', to: (params, query, search) => `/packages${search}` },
  {
    path: '/host-vulnerabilities/:host_id',
    to: ({ host_id: id }, query) => withQuery(hostPath({ id }, 'vulnerabilities'), {
      q: query.get('kw'),
      fix: oneOf(query.get('status'), ['fixed', 'unfixed']),
    }),
  },
  {
    path: '/packages-for-host/:host_id',
    to: ({ host_id: id }, query) => withQuery(hostPath({ id }, 'packages'), {
      package: query.get('kw'),
      version: oneOf(query.get('outdated'), ['outdated', 'latest']),
    }),
  },
  { path: '/hosts-for-packages', to: (params, query) => packageTab(query, 'hosts', { host: query.get('kw') }) },
  { path: '/versions-for-packages', to: (params, query) => packageTab(query, 'versions') },
  { path: '/vulnerabilities-for-packages', to: (params, query) => packageTab(query, 'vulnerabilities', { cve: query.get('kw') }) },
  {
    // The Same source page listed packages whose name contains the source name.
    path: '/child-packages',
    to: (params, query) => withQuery('/packages', {
      kw: query.get('kw') || query.get('package'),
      mode: oneOf(query.get('mode'), ['bsg_only', 'enterprise_only', 'with_vulns']),
      outdated: oneOf(query.get('outdated'), ['outdated', 'latest']),
      multi: query.get('multi') ? '1' : '',
      env: oneOf(query.get('env'), ['bsg', 'enterprise']),
    }),
  },
  {
    path: '/packages',
    exact: true,
    when: (query) => query.get('for_host'),
    to: (params, query) => withQuery(hostPath({ id: query.get('for_host') }, 'packages'), {
      package: query.get('kw'),
      version: oneOf(query.get('outdated'), ['outdated', 'latest']),
    }),
  },
  {
    // "CVE-2024-5535/focal": the app's own links always sent the codename "all".
    path: '/hosts',
    exact: true,
    when: (query) => query.get('mode') === 'hosts_for_vuln' && query.get('cve_and_codename'),
    to: (params, query) => {
      const value = query.get('cve_and_codename');
      const at = value.lastIndexOf('/');
      return cvePath(at > -1 ? value.slice(0, at) : value, 'hosts');
    },
  },
  {
    path: '/hosts',
    exact: true,
    when: (query) => query.get('pkg'),
    to: (params, query) => withQuery(packagePath({ id: query.get('pkg') }, 'hosts'), { host: query.get('kw') }),
  },
  {
    path: '/vulnerabilities',
    exact: true,
    when: (query) => query.get('package_uuid'),
    to: (params, query) => withQuery(packagePath({ id: query.get('package_uuid') }, 'vulnerabilities'), { cve: query.get('kw') }),
  },
];

export default LEGACY_REDIRECTS;
